import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { crearFactura, actualizarFactura, obtenerSiguienteFolio, timbrarFactura, obtenerDetalleFactura, cancelarFactura, obtenerTipoCambioBanxico } from "../services/facturaService";
import { buscarClientePorClave } from "../services/clienteService";
import { buscarArticuloPorClave } from "../services/articuloService";
import { obtenerCotizacion, obtenerCotizaciones } from "../services/cotizacionService";
import Toast, { useToast } from "../components/Toast";

/* ===== Helpers ===== */
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Extrae un campo de un objeto probando múltiples casings
const pick = (obj, ...keys) => {
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  return "";
};
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtMX = (n) =>
  round2(n).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const USO_CFDI_OPTS = [
  { v: "G01", l: "G01 – Adquisicion de mercancias" },
  { v: "G02", l: "G02 – Devoluciones, descuentos o bonificaciones" },
  { v: "G03", l: "G03 – Gastos en general" },
  { v: "I01", l: "I01 – Construcciones" },
  { v: "I02", l: "I02 – Mobiliario y equipo de oficina" },
  { v: "I03", l: "I03 – Equipo de transporte" },
  { v: "I04", l: "I04 – Equipo de computo y accesorios" },
  { v: "I06", l: "I06 – Comunicaciones telefonicas" },
  { v: "I08", l: "I08 – Otra maquinaria y equipo" },
  { v: "D01", l: "D01 – Honorarios medicos" },
  { v: "D10", l: "D10 – Pagos por servicios educativos" },
  { v: "P01", l: "P01 – Por definir" },
  { v: "S01", l: "S01 – Sin efectos fiscales" },
  { v: "CP01", l: "CP01 – Pagos" },
  { v: "CN01", l: "CN01 – Nomina" },
];

const EMPTY_ITEM = {
  articulo: "",
  cantidad: 1,
  unidadMedida: "PZA",
  descripcion: "",
  precioUnitario: 0,
  codigoSat: "01010101",
  claveUnidad: "E48",
  descuento: 0,
};
const ensureItem = (x) => ({ ...EMPTY_ITEM, ...(x || {}) });

/* ===== Input de celda de tabla ===== */
const CellInput = ({ value, onChange, onBlur, onKeyDown, placeholder, type = "text", align = "left" }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={onChange}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    step={type === "number" ? "0.0001" : undefined}
    style={{
      width: "100%",
      padding: "5px 7px",
      border: "1px solid var(--clr-border)",
      borderRadius: "var(--radius-sm)",
      fontSize: "var(--fs-sm)",
      fontFamily: "var(--font-sans)",
      background: "#fff",
      outline: "none",
      textAlign: align,
      boxSizing: "border-box",
    }}
    onFocus={(e) => {
      e.target.style.borderColor = "var(--clr-primary)";
      e.target.style.boxShadow = "var(--shadow-focus)";
    }}
    onBlurCapture={(e) => {
      e.target.style.borderColor = "var(--clr-border)";
      e.target.style.boxShadow = "none";
    }}
  />
);

export default function FacturaFormPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { serie: editSerie, factura: editFactura, empresa: editEmpresa } = useParams();
  const modoEdicion = !!(editSerie && editFactura);

  const empresaId = Number(editEmpresa || localStorage.getItem("empresaId") || 1);

  const [form, setForm] = useState({
    serie: "A",
    fecha: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    moneda: "P",
    tipoCambio: "",
    cliente: "",
    nombreCliente: "",
    rfc: "",
    cotizacion: "",
    ordenServicio: "",
    llevaRetencion: false,
    aplicaDescuento: false,
    porDescto: 0,
    notasAdicionales: "",
    observaciones: "",
    usoCfdi: "G03",
    metodoPago: "PPD",
  });

  const [nextFactura, setNextFactura] = useState(null);
  const [folioLoading, setFolioLoading] = useState(false);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [guardandoCambios, setGuardandoCambios] = useState(false);
  const [timbrandoId, setTimbrandoId] = useState(null); // {serie, factura} after save
  const [timbrandoBusy, setTimbrandoBusy] = useState(false);
  const [timbradoInfo, setTimbradoInfo] = useState(null); // UUID result
  const [clienteMsg, setClienteMsg] = useState("");
  const [clienteLoading, setClienteLoading] = useState(false);
  const [rowMsgs, setRowMsgs] = useState({});
  const [cotizacionLoading, setCotizacionLoading] = useState(false);
  const [facturaStatus, setFacturaStatus] = useState("A");
  const [facturaEsTimbrada, setFacturaEsTimbrada] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [tipoCambioLoading, setTipoCambioLoading] = useState(false);
  const [tipoCambioMsg, setTipoCambioMsg] = useState("");

  /* Trae el FIX de Banxico para la fecha capturada y lo pone en el campo */
  const usarTipoCambioBanxico = useCallback(async () => {
    setTipoCambioLoading(true);
    setTipoCambioMsg("");
    try {
      const { fecha, valor } = await obtenerTipoCambioBanxico(form.fecha);
      setForm((f) => ({ ...f, tipoCambio: valor }));
      setTipoCambioMsg(fecha === form.fecha ? "" : `Sin publicación el ${form.fecha}; se usó el FIX del ${fecha}.`);
    } catch (err) {
      setTipoCambioMsg(err?.response?.data?.message || "No se pudo obtener el tipo de cambio de Banxico.");
    } finally {
      setTipoCambioLoading(false);
    }
  }, [form.fecha]);

  /* Cargar siguiente folio */
  const cargarFolio = useCallback(async (serie) => {
    setFolioLoading(true);
    try {
      const n = await obtenerSiguienteFolio(serie || "A");
      setNextFactura(n);
    } catch {
      setNextFactura(null);
    } finally {
      setFolioLoading(false);
    }
  }, []);

  useEffect(() => { if (!modoEdicion) cargarFolio(form.serie); }, [form.serie, cargarFolio, modoEdicion]);

  /* Cargar datos en modo edición */
  useEffect(() => {
    if (!modoEdicion) return;
    (async () => {
      try {
        const det = await obtenerDetalleFactura({ serie: editSerie, factura: Number(editFactura), empresa: empresaId });
        setForm(f => ({
          ...f,
          serie: det.serie ?? editSerie,
          cliente: det.claveCliente ?? "",
          nombreCliente: det.nombreCliente ?? "",
          rfc: det.rfc ?? "",
          fecha: det.fechaMovimiento ? det.fechaMovimiento.slice(0, 10) : f.fecha,
          moneda: det.moneda === "USD" || det.moneda === "D" ? "D" : "P",
          tipoCambio: det.tipoCambio ?? "",
          usoCfdi: det.usoCfdi ?? f.usoCfdi,
          metodoPago: det.condicionesPago ?? f.metodoPago,
        }));
        setNextFactura(Number(editFactura));
        setTimbrandoId({ serie: editSerie, factura: Number(editFactura) });
        setFacturaStatus(det.status ?? "A");
        setFacturaEsTimbrada(!!det.folioFiscalUuid);
        if (Array.isArray(det.partidas) && det.partidas.length) {
          setItems(det.partidas.map(p => ({
            articulo: p.claveArticulo ?? "",
            cantidad: p.cantidad ?? 1,
            unidadMedida: p.unidad ?? "PZA",
            descripcion: p.descripcion ?? "",
            precioUnitario: p.precio ?? 0,
            codigoSat: p.codigoSat ?? "81111508",
            claveUnidad: p.claveUnidad ?? "E48",
            descuento: 0,
          })));
        }
      } catch {
        toast.error("No se pudo cargar la factura para edición.");
      }
    })();
  }, [modoEdicion, editSerie, editFactura, empresaId]);

  const onChangeHeader = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : name === "porDescto" ? Number(value) : value,
    }));
  };

  const setRowMsg = (idx, msg) => setRowMsgs((m) => ({ ...m, [idx]: msg || "" }));
  const addRow = () => setItems((arr) => [...arr, { ...EMPTY_ITEM }]);
  const rmRow = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const onChangeItem = (idx, name, value) => {
    setItems((arr) => {
      const next = arr.slice();
      next[idx] = ensureItem(next[idx]);
      const numericFields = ["cantidad", "precioUnitario", "descuento"];
      next[idx][name] = numericFields.includes(name) ? Number(value) : value ?? "";
      return next;
    });
  };

  /* Lookup de cliente */
  const onClienteBlur = async () => {
    const clave = form.cliente?.trim();
    if (!clave) { setClienteMsg(""); setForm((f) => ({ ...f, nombreCliente: "", rfc: "" })); return; }
    setClienteMsg(""); setClienteLoading(true);
    try {
      const cli = await buscarClientePorClave(empresaId, clave);
      if (!cli) {
        setForm((f) => ({ ...f, nombreCliente: "", rfc: "" }));
        setClienteMsg("El cliente no existe.");
        return;
      }
      setForm((f) => ({
        ...f,
        nombreCliente: pick(cli, "NombreFiscal", "Nombre", "nombre", "nombreFiscal", "RazonSocial", "razonSocial") || clave,
        rfc:        pick(cli, "Rfc", "rfc", "RFC"),
        usoCfdi:    pick(cli, "UsoCfdi", "usoCfdi", "usocfdi", "UsoCFDI") || f.usoCfdi,
        metodoPago: pick(cli, "MetodoPago", "metodoPago", "metodopago") || f.metodoPago,
      }));
    } catch {
      setForm((f) => ({ ...f, nombreCliente: "", rfc: "" }));
      setClienteMsg("No se pudo validar el cliente.");
    } finally {
      setClienteLoading(false);
    }
  };

  /* Lookup de articulo */
  const lookupArticulo = async (idx) => {
    const clave = (ensureItem(items[idx]).articulo || "").trim();
    if (!clave) { setRowMsg(idx, ""); return; }
    setRowMsg(idx, "Buscando...");
    try {
      const art = await buscarArticuloPorClave(empresaId, clave);
      setItems((arr) =>
        arr.map((raw, i) => {
          if (i !== idx) return raw;
          const it = ensureItem(raw);
          return {
            ...it,
            descripcion: art?.descripcion ?? it.descripcion,
            unidadMedida: art?.unidadMedida ?? it.unidadMedida,
            precioUnitario: Number.isFinite(Number(art?.precioUnitario ?? art?.precio))
              ? Number(art?.precioUnitario ?? art?.precio) : it.precioUnitario,
            codigoSat: art?.codSat || art?.CodSat || art?.codigoSat || art?.CodigoSat || it.codigoSat || "01010101",
            claveUnidad: art?.claveUnidad ?? art?.ClaveUnidad ?? it.claveUnidad,
          };
        })
      );
      setRowMsg(idx, "");
    } catch {
      setRowMsg(idx, "Articulo no encontrado.");
    }
  };

  /* Cargar desde cotizacion */
  const cargarDesdeCotizacion = async () => {
    const numCot = form.cotizacion?.trim();
    if (!numCot) return toast.warning("Ingresa el numero de cotizacion primero.");
    setCotizacionLoading(true);
    try {
      const listRes = await obtenerCotizaciones(1, 20, numCot);
      const lista = listRes?.data?.Data ?? listRes?.data?.data ?? [];
      const encontrada = lista.find(
        (c) => String(c.Cotizacion ?? c.cotizacion) === String(numCot)
      );
      if (!encontrada) return toast.warning(`Cotizacion ${numCot} no encontrada.`);

      const res = await obtenerCotizacion(encontrada.Id ?? encontrada.id);
      const cot = res?.data ?? res;
      if (!cot) return toast.error("No se pudo obtener el detalle de la cotizacion.");

      if (cot.Cliente || cot.cliente) {
        const clave = cot.Cliente ?? cot.cliente;
        setForm((f) => ({
          ...f,
          cliente: clave,
          nombreCliente: cot.NombreCliente ?? cot.nombreCliente ?? clave,
          moneda: cot.Moneda ?? cot.moneda ?? f.moneda,
          notasAdicionales: cot.NotasAdicionales ?? cot.notasAdicionales ?? f.notasAdicionales,
          observaciones: cot.Observaciones ?? cot.observaciones ?? f.observaciones,
        }));

        // Traer datos fiscales del cliente (usoCfdi, metodoPago, rfc)
        try {
          const cli = await buscarClientePorClave(empresaId, clave);
          if (cli) {
            setForm((f) => ({
              ...f,
              rfc:        pick(cli, "Rfc", "rfc", "RFC") || f.rfc,
              usoCfdi:    pick(cli, "UsoCfdi", "usoCfdi", "usocfdi", "UsoCFDI") || f.usoCfdi,
              metodoPago: pick(cli, "MetodoPago", "metodoPago", "metodopago") || f.metodoPago,
            }));
          }
        } catch (e) {
          console.error("Error lookup cliente en cotización:", e);
        }
      }

      const detalles = cot.CotizacionesDetalle ?? cot.cotizacionesDetalle ?? [];
      if (detalles.length > 0) {
        setItems(
          detalles.map((d) => {
            const qty = Number(d.Cantidad ?? d.cantidad ?? 1) || 1;
            const precioNeto =
              Number(d.Preciounitariocondescuento ?? d.preciounitariocondescuento) ||
              Number(d.Importecondescuento ?? d.importecondescuento) / qty ||
              Number(d.Importe ?? d.importe) / qty ||
              Number(d.PrecioUnitario ?? d.precioUnitario) ||
              0;
            return {
              articulo: d.Articulo ?? d.articulo ?? "",
              cantidad: qty,
              unidadMedida: d.Unidadmedida ?? d.unidadmedida ?? "PZA",
              descripcion: d.Descripcion ?? d.descripcion ?? "",
              precioUnitario: Math.round(precioNeto * 100) / 100,
              codigoSat: d.codSat || d.CodSat || "01010101",
              claveUnidad: d.claveUnidad || d.ClaveUnidad || "E48",
              descuento: 0,
            };
          })
        );
      }
    } catch {
      toast.error("No se pudo cargar la cotizacion.");
    } finally {
      setCotizacionLoading(false);
    }
  };

  /* Calculos */
  const subtotalBruto = useMemo(() =>
    round2(items.filter(Boolean).reduce((s, raw) => {
      const it = ensureItem(raw);
      return s + num(it.cantidad) * num(it.precioUnitario);
    }, 0)),
    [items]
  );
  const descuento = useMemo(() =>
    (!form.aplicaDescuento || !num(form.porDescto)) ? 0
      : round2(subtotalBruto * (num(form.porDescto) / 100)),
    [subtotalBruto, form.aplicaDescuento, form.porDescto]
  );
  const subtotal = useMemo(() => round2(subtotalBruto - descuento), [subtotalBruto, descuento]);
  const iva = useMemo(() => round2(subtotal * 0.16), [subtotal]);
  // Honorarios: ISR 10% + IVA retenido 10.6667%
  const retencion = useMemo(() => form.llevaRetencion ? round2(subtotal * 0.10) : 0, [subtotal, form.llevaRetencion]);
  const retencion2 = useMemo(() => form.llevaRetencion ? round2(subtotal * 0.106667) : 0, [subtotal, form.llevaRetencion]);
  const total = useMemo(() => round2(subtotal + iva - retencion - retencion2), [subtotal, iva, retencion, retencion2]);

  /* Guardar factura */
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente?.trim()) return toast.warning("Captura la clave del cliente.");
    if (!form.nombreCliente?.trim()) return toast.warning("El cliente no existe o no ha sido validado.");
    if (form.moneda === "D" && !(num(form.tipoCambio) > 0)) return toast.warning("Captura el tipo de cambio.");
    const detalles = items.filter(Boolean).map(ensureItem).filter((it) => it.articulo);
    if (!detalles.length) return toast.warning("Agrega al menos un renglon con clave de articulo.");

    setSaving(true);
    try {
      const result = await crearFactura({
        serie: form.serie || "A",
        cliente: form.cliente,
        fecha: form.fecha,
        moneda: form.moneda,
        tipoCambio: form.moneda === "D" ? num(form.tipoCambio) : null,
        llevaRetencion: form.llevaRetencion,
        aplicaDescuento: form.aplicaDescuento,
        porDescto: num(form.porDescto),
        notasAdicionales: form.notasAdicionales,
        observaciones: form.observaciones,
        cotizacion: form.cotizacion || "",
        ordenServicio: form.ordenServicio || "",
        empresa: empresaId,
        usoCfdi: form.usoCfdi || "G03",
        metodoPago: form.metodoPago || "PPD",
        detalles: detalles.map((it) => ({
          articulo: it.articulo,
          cantidad: num(it.cantidad),
          unidadMedida: it.unidadMedida || "PZA",
          descripcion: it.descripcion || "",
          precioUnitario: num(it.precioUnitario),
          codigoSat: parseInt(it.codigoSat, 10) || 0,
          claveUnidad: it.claveUnidad || "E48",
          descuento: num(it.descuento) || 0,
        })),
      });
      const serieGuardada = result?.serie ?? form.serie;
      const facturaGuardada = result?.factura;
      setTimbrandoId({ serie: serieGuardada, factura: facturaGuardada });
      setTimbradoInfo(null);
      toast.success(`Factura ${serieGuardada}-${facturaGuardada ?? ""} creada. Ahora puedes timbrarla.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          "No se pudo crear la factura."
      );
    } finally {
      setSaving(false);
    }
  };

  /* Guardar cambios en modo edición */
  const onGuardarCambios = async () => {
    if (!form.cliente?.trim()) return toast.warning("Captura la clave del cliente.");
    if (!form.nombreCliente?.trim()) return toast.warning("El cliente no existe o no ha sido validado.");
    if (form.moneda === "D" && !(num(form.tipoCambio) > 0)) return toast.warning("Captura el tipo de cambio.");
    const detalles = items.filter(Boolean).map(ensureItem).filter((it) => it.articulo);
    if (!detalles.length) return toast.warning("Agrega al menos un renglon con clave de articulo.");

    setGuardandoCambios(true);
    try {
      await actualizarFactura({
        serie: editSerie,
        factura: Number(editFactura),
        empresa: empresaId,
        cliente: form.cliente,
        fecha: form.fecha,
        moneda: form.moneda,
        tipoCambio: form.moneda === "D" ? num(form.tipoCambio) : null,
        llevaRetencion: form.llevaRetencion,
        aplicaDescuento: form.aplicaDescuento,
        porDescto: num(form.porDescto),
        notasAdicionales: form.notasAdicionales,
        observaciones: form.observaciones,
        cotizacion: form.cotizacion || "",
        ordenServicio: form.ordenServicio || "",
        usoCfdi: form.usoCfdi || "G03",
        metodoPago: form.metodoPago || "PPD",
        detalles: detalles.map((it) => ({
          articulo: it.articulo,
          cantidad: num(it.cantidad),
          unidadMedida: it.unidadMedida || "PZA",
          descripcion: it.descripcion || "",
          precioUnitario: num(it.precioUnitario),
          codigoSat: parseInt(it.codigoSat, 10) || 0,
          claveUnidad: it.claveUnidad || "E48",
          descuento: num(it.descuento) || 0,
        })),
      });
      toast.success(`Factura ${editSerie}-${editFactura} actualizada correctamente.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          "No se pudo actualizar la factura."
      );
    } finally {
      setGuardandoCambios(false);
    }
  };

  /* Timbrar CFDI */
  const onTimbrar = async () => {
    if (!timbrandoId) return;
    setTimbrandoBusy(true);
    try {
      const res = await timbrarFactura({ ...timbrandoId, empresa: empresaId });
      setTimbradoInfo(res);
      toast.success(`Factura timbrada correctamente. UUID: ${res.uuid}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          "Error al timbrar la factura."
      );
    } finally {
      setTimbrandoBusy(false);
    }
  };

  const handleCancelarFactura = async () => {
    setCancelando(true);
    setCancelError(null);
    try {
      await cancelarFactura({ serie: editSerie, factura: Number(editFactura), empresa: empresaId });
      setShowConfirmCancel(false);
      setFacturaStatus("C");
    } catch (err) {
      setCancelError(err?.response?.data?.detail || err?.response?.data?.message || err.message || "Error al cancelar.");
    } finally {
      setCancelando(false);
    }
  };

  const cellStyle = { padding: "8px 10px", borderBottom: "1px solid var(--clr-border-subtle)", verticalAlign: "middle" };
  const cellNum = { ...cellStyle, textAlign: "right" };

  const colHeaders = ["Articulo", "Cant.", "U. Med.", "Descripcion", "Cod. SAT", "Clave Uni.", "P. Unitario", "Importe", ""];

  return (
    <div style={{ maxWidth: 1440 }}>
      <Toast toasts={toast.toasts} onClose={toast.closeToast} />
      <div className="lmd-page-header">
        <div className="lmd-page-header-row">
          <div>
            <h1 className="lmd-page-title">{modoEdicion ? `Editar Factura ${editSerie}-${editFactura}` : "Nueva Factura"}</h1>
            <p className="lmd-page-subtitle">
              {modoEdicion ? "Modifica los datos y/o timbra el CFDI 4.0" : "Captura los datos del encabezado, renglones y timbra el CFDI 4.0"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} style={{ display: "grid", gap: "var(--sp-4)" }}>

        {/* ── ENCABEZADO ── */}
        <div className="lmd-card">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">Datos del encabezado</h3>
          </div>
          <div className="lmd-card-body" style={{ display: "grid", gap: "var(--sp-4)" }}>

            {/* Fila 1: Serie / Factura / Cliente / Nombre */}
            <div style={{ display: "grid", gridTemplateColumns: "80px 140px 1fr 2fr", gap: "var(--sp-4)" }}>
              <div className="lmd-field">
                <label className="lmd-label">Serie</label>
                <input className="lmd-input" name="serie" value={form.serie} onChange={onChangeHeader}
                  maxLength={1} style={{ textTransform: "uppercase" }} onBlur={() => cargarFolio(form.serie)} />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">No. Factura</label>
                <input className="lmd-input" value={folioLoading ? "..." : (nextFactura ?? "")} readOnly
                  style={{ background: "var(--clr-bg)", fontWeight: "var(--fw-semibold)", color: "var(--clr-primary)" }} />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Clave Cliente *</label>
                <input className="lmd-input" name="cliente" value={form.cliente} onChange={onChangeHeader}
                  onBlur={onClienteBlur}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onClienteBlur(); } }}
                  placeholder="Clave del cliente" />
                {clienteLoading && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: "var(--fs-xs)", color: "var(--clr-text-muted)" }}>
                    <div className="lmd-spinner" style={{ width: 12, height: 12 }} /> Buscando...
                  </div>
                )}
                {clienteMsg && <p style={{ margin: "4px 0 0", fontSize: "var(--fs-xs)", color: "var(--clr-danger)" }}>{clienteMsg}</p>}
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Nombre del cliente</label>
                <input className="lmd-input" value={form.nombreCliente} readOnly
                  placeholder="Se llena al capturar la clave"
                  style={{ background: form.nombreCliente ? "#fff" : "var(--clr-bg)" }} />
              </div>
            </div>

            {/* Fila 2: Fecha / Moneda / RFC / MetodoPago / UsoCFDI */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1.5fr", gap: "var(--sp-4)" }}>
              <div className="lmd-field">
                <label className="lmd-label">Fecha</label>
                <input className="lmd-input" type="date" name="fecha" value={form.fecha} onChange={onChangeHeader} />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Moneda</label>
                <select
                  className="lmd-select"
                  name="moneda"
                  value={form.moneda}
                  onChange={(e) => {
                    const moneda = e.target.value;
                    setForm((f) => ({ ...f, moneda }));
                    if (moneda === "D" && !form.tipoCambio && !modoEdicion) usarTipoCambioBanxico();
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="P">MXN — Pesos</option>
                  <option value="D">USD — Dolares</option>
                </select>
              </div>
              {form.moneda === "D" && (
                <div className="lmd-field">
                  <label className="lmd-label">Tipo de cambio</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      className="lmd-input"
                      type="number"
                      step="0.000001"
                      name="tipoCambio"
                      value={form.tipoCambio}
                      onChange={onChangeHeader}
                      placeholder="Pesos por dólar"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="lmd-btn lmd-btn-secondary"
                      onClick={usarTipoCambioBanxico}
                      disabled={tipoCambioLoading}
                      title="Usar el FIX de Banxico para la fecha capturada"
                      style={{ whiteSpace: "nowrap", fontSize: "var(--fs-xs)", padding: "0 10px" }}
                    >
                      {tipoCambioLoading ? "..." : "Banxico"}
                    </button>
                  </div>
                  {tipoCambioMsg && (
                    <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--clr-text-secondary)" }}>{tipoCambioMsg}</p>
                  )}
                </div>
              )}
              <div className="lmd-field">
                <label className="lmd-label">R.F.C.</label>
                <input className="lmd-input" value={form.rfc} readOnly style={{ background: "var(--clr-bg)" }} />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Metodo de Pago</label>
                <select className="lmd-select" name="metodoPago" value={form.metodoPago} onChange={onChangeHeader} style={{ width: "100%" }}>
                  <option value="PPD">PPD – Pago en Parcialidades</option>
                  <option value="PUE">PUE – Pago en una sola Exhibicion</option>
                </select>
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Uso CFDI</label>
                <select className="lmd-select" name="usoCfdi" value={form.usoCfdi} onChange={onChangeHeader} style={{ width: "100%" }}>
                  {USO_CFDI_OPTS.map((o) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Fila 3: Cotizacion / Orden Servicio */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--sp-4)" }}>
              <div className="lmd-field">
                <label className="lmd-label">No. Cotizacion (referencia)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="lmd-input" name="cotizacion" value={form.cotizacion} onChange={onChangeHeader}
                    placeholder="Numero de cotizacion" style={{ flex: 1 }} />
                  <button type="button" className="lmd-btn lmd-btn-secondary"
                    onClick={cargarDesdeCotizacion} disabled={cotizacionLoading || !form.cotizacion}
                    style={{ whiteSpace: "nowrap", fontSize: "var(--fs-xs)", padding: "0 12px" }}>
                    {cotizacionLoading ? "..." : "Cargar"}
                  </button>
                </div>
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Orden de Servicio</label>
                <input className="lmd-input" name="ordenServicio" value={form.ordenServicio}
                  onChange={onChangeHeader} placeholder="Numero de orden" />
              </div>
              <div />
            </div>

            {/* Fila 4: Descuento / Retencion */}
            <div style={{ display: "grid", gridTemplateColumns: "auto auto 1fr", gap: "var(--sp-6)", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
                  <input type="checkbox" name="aplicaDescuento" checked={form.aplicaDescuento} onChange={onChangeHeader} />
                  <span style={{ fontSize: "var(--fs-sm)", fontWeight: "var(--fw-medium)" }}>Aplica Descuento</span>
                </label>
                {form.aplicaDescuento && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input className="lmd-input" type="number" name="porDescto" value={form.porDescto}
                      onChange={onChangeHeader} min={0} max={100} step="0.01" style={{ width: 90 }} />
                    <span style={{ fontSize: "var(--fs-sm)" }}>%</span>
                  </div>
                )}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox" name="llevaRetencion" checked={form.llevaRetencion} onChange={onChangeHeader} />
                <span style={{ fontSize: "var(--fs-sm)", fontWeight: "var(--fw-medium)" }}>Lleva Retencion (Honorarios)</span>
              </label>
              <div />
            </div>
          </div>
        </div>

        {/* ── DETALLE ── */}
        <div className="lmd-card">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">Detalle de renglones</h3>
            <button type="button" className="lmd-btn lmd-btn-primary" onClick={addRow}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Agregar renglon
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "6%" }} />
                <col />
                <col style={{ width: "8%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "3%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: "var(--clr-bg)", borderBottom: "2px solid var(--clr-border)" }}>
                  {colHeaders.map((h, i) => (
                    <th key={i} style={{
                      padding: "9px 10px",
                      fontSize: "var(--fs-xs)",
                      fontWeight: "var(--fw-semibold)",
                      color: "var(--clr-text-secondary)",
                      textTransform: "uppercase",
                      letterSpacing: ".4px",
                      textAlign: i >= 6 ? "right" : "left",
                      whiteSpace: "nowrap",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.filter(Boolean).map((raw, idx) => {
                  const it = ensureItem(raw);
                  const importe = round2(num(it.cantidad) * num(it.precioUnitario));
                  const isEven = idx % 2 === 0;

                  return (
                    <tr key={idx} style={{ background: isEven ? "#fff" : "#fafbfc" }}>
                      <td style={cellStyle}>
                        <CellInput value={it.articulo} placeholder="Clave"
                          onChange={(e) => onChangeItem(idx, "articulo", e.target.value)}
                          onBlur={() => lookupArticulo(idx)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupArticulo(idx); } }} />
                        {rowMsgs[idx] && (
                          <p style={{ margin: "3px 0 0", fontSize: 11, color: rowMsgs[idx].includes("no") ? "var(--clr-danger)" : "var(--clr-text-muted)" }}>
                            {rowMsgs[idx]}
                          </p>
                        )}
                      </td>
                      <td style={cellNum}>
                        <CellInput value={it.cantidad} type="number" align="right"
                          onChange={(e) => onChangeItem(idx, "cantidad", e.target.value)} />
                      </td>
                      <td style={cellStyle}>
                        <CellInput value={it.unidadMedida} placeholder="PZA"
                          onChange={(e) => onChangeItem(idx, "unidadMedida", e.target.value)} />
                      </td>
                      <td style={cellStyle}>
                        <CellInput value={it.descripcion} placeholder="Descripcion del producto o servicio"
                          onChange={(e) => onChangeItem(idx, "descripcion", e.target.value)} />
                      </td>
                      <td style={cellStyle}>
                        <CellInput value={it.codigoSat} placeholder="01010101"
                          onChange={(e) => onChangeItem(idx, "codigoSat", e.target.value)} />
                      </td>
                      <td style={cellStyle}>
                        <CellInput value={it.claveUnidad} placeholder="E48"
                          onChange={(e) => onChangeItem(idx, "claveUnidad", e.target.value)} />
                      </td>
                      <td style={cellNum}>
                        <CellInput value={it.precioUnitario} type="number" align="right"
                          onChange={(e) => onChangeItem(idx, "precioUnitario", e.target.value)} />
                      </td>
                      <td style={{ ...cellNum, fontVariantNumeric: "tabular-nums", fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)", fontWeight: "var(--fw-semibold)" }}>
                        {fmtMX(importe)}
                      </td>
                      <td style={{ ...cellStyle, textAlign: "center" }}>
                        <button type="button" onClick={() => rmRow(idx)}
                          style={{ border: "none", background: "none", cursor: "pointer", color: "var(--clr-danger)", padding: 4, borderRadius: "var(--radius-sm)", fontSize: 16, lineHeight: 1 }}
                          title="Quitar renglon">×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── NOTAS ── */}
        <div className="lmd-card">
          <div className="lmd-card-header"><h3 className="lmd-card-title">Notas y observaciones</h3></div>
          <div className="lmd-card-body" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "var(--sp-4)" }}>
            <div className="lmd-field">
              <label className="lmd-label">Notas adicionales</label>
              <textarea className="lmd-input" name="notasAdicionales" value={form.notasAdicionales}
                onChange={onChangeHeader} rows={3} style={{ resize: "vertical", fontFamily: "var(--font-sans)" }} />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Observaciones (max 50 car.)</label>
              <input className="lmd-input" name="observaciones" value={form.observaciones}
                onChange={onChangeHeader} maxLength={50} />
            </div>
          </div>
        </div>

        {/* ── TOTALES ── */}
        <div className="lmd-card">
          <div className="lmd-card-body">
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 340, display: "grid", gap: "var(--sp-2)" }}>
                {[
                  { label: "Subtotal bruto", value: subtotalBruto, show: form.aplicaDescuento },
                  { label: `Descuento (${num(form.porDescto)}%)`, value: descuento, show: form.aplicaDescuento },
                  { label: "Subtotal", value: subtotal, show: true },
                  { label: "IVA 16%", value: iva, show: true },
                  { label: "ISR retenido (10%)", value: retencion, show: form.llevaRetencion },
                  { label: "IVA retenido (10.6667%)", value: retencion2, show: form.llevaRetencion },
                ]
                  .filter((r) => r.show)
                  .map(({ label, value }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--clr-border-subtle)" }}>
                      <span style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-secondary)" }}>{label}</span>
                      <span style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", fontSize: "var(--fs-sm)" }}>
                        ${fmtMX(value)}
                      </span>
                    </div>
                  ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0 4px" }}>
                  <span style={{ fontWeight: "var(--fw-bold)", fontSize: "var(--fs-md)" }}>Total</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: "var(--fw-extrabold)", fontSize: "var(--fs-xl)", color: "var(--clr-primary)" }}>
                    ${fmtMX(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RESULTADO TIMBRADO ── */}
        {timbradoInfo && (
          <div className="lmd-card" style={{ border: "2px solid var(--clr-success, #16a34a)" }}>
            <div className="lmd-card-body">
              <p style={{ fontWeight: "var(--fw-semibold)", color: "var(--clr-success, #16a34a)", marginBottom: 8 }}>
                Factura Timbrada Correctamente
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 16px", fontSize: "var(--fs-sm)" }}>
                <span style={{ color: "var(--clr-text-secondary)", fontWeight: "var(--fw-medium)" }}>UUID:</span>
                <span style={{ fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{timbradoInfo.uuid}</span>
                <span style={{ color: "var(--clr-text-secondary)", fontWeight: "var(--fw-medium)" }}>Fecha timbrado:</span>
                <span>{timbradoInfo.fechaTimbrado}</span>
                <span style={{ color: "var(--clr-text-secondary)", fontWeight: "var(--fw-medium)" }}>No. Cert. SAT:</span>
                <span style={{ fontFamily: "var(--font-mono)" }}>{timbradoInfo.noCertificadoSAT}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── ACCIONES ── */}
        <div style={{ display: "flex", gap: "var(--sp-3)", paddingBottom: "var(--sp-8)", flexWrap: "wrap" }}>
          {!timbrandoId ? (
            <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
              {saving ? (
                <><div className="lmd-spinner" style={{ width: 14, height: 14 }} /> Guardando...</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                  </svg>
                  Guardar Factura
                </>
              )}
            </button>
          ) : (
            <>
              {modoEdicion && !timbradoInfo && (
                <button type="button" className="lmd-btn lmd-btn-secondary"
                  onClick={onGuardarCambios} disabled={guardandoCambios || timbrandoBusy}>
                  {guardandoCambios ? (
                    <><div className="lmd-spinner" style={{ width: 14, height: 14 }} /> Guardando...</>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                      </svg>
                      Guardar cambios
                    </>
                  )}
                </button>
              )}
              <button type="button" className="lmd-btn lmd-btn-primary"
                onClick={onTimbrar} disabled={timbrandoBusy || !!timbradoInfo}>
                {timbrandoBusy ? (
                  <><div className="lmd-spinner" style={{ width: 14, height: 14 }} /> Timbrando...</>
                ) : timbradoInfo ? (
                  "Ya timbrada"
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                    Timbrar CFDI 4.0
                  </>
                )}
              </button>
              <button type="button" className="lmd-btn lmd-btn-secondary"
                onClick={() => navigate("/facturacion/factura40")}>
                Ir a Facturas
              </button>
              {modoEdicion && facturaStatus !== "C" && (
                <button type="button"
                  onClick={() => { setCancelError(null); setShowConfirmCancel(true); }}
                  style={{
                    fontSize: 13, fontWeight: 700, padding: "8px 16px", borderRadius: 4,
                    background: "#dc2626", color: "#fff", border: "none", cursor: "pointer",
                  }}>
                  Cancelar Factura
                </button>
              )}
              {modoEdicion && facturaStatus === "C" && (
                <span style={{
                  fontSize: 12, fontWeight: 700, padding: "6px 14px", borderRadius: 99,
                  background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5",
                  display: "inline-flex", alignItems: "center",
                }}>
                  CANCELADA
                </span>
              )}
            </>
          )}
          {!timbrandoId && (
            <button type="button" className="lmd-btn lmd-btn-secondary" onClick={() => navigate("/facturacion/factura40")}>
              Cancelar
            </button>
          )}
        </div>
      </form>

      {/* MODAL CONFIRMACIÓN CANCELACIÓN */}
      {showConfirmCancel && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowConfirmCancel(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center",
          }}
        >
          <div style={{
            background: "#fff", borderRadius: 8, padding: 28, width: "90%", maxWidth: 440,
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 700, color: "#111" }}>
              Confirmar Cancelación
            </h3>
            <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151" }}>
              Factura: <strong>{editSerie}-{editFactura}</strong> — {form.nombreCliente}
            </p>
            {facturaEsTimbrada ? (
              <div style={{
                background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6,
                padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#92400e",
              }}>
                Esta factura tiene <strong>timbre fiscal (CFDI)</strong>. Se enviará la solicitud de cancelación al SAT a través del PAC.
              </div>
            ) : (
              <div style={{
                background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6,
                padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#0c4a6e",
              }}>
                Esta factura <strong>no tiene timbre fiscal</strong>. Se cancelará únicamente en la base de datos.
              </div>
            )}
            {cancelError && (
              <div style={{
                background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6,
                padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#b91c1c",
              }}>
                {cancelError}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setShowConfirmCancel(false)}
                disabled={cancelando}
                style={{
                  padding: "8px 20px", borderRadius: 4, border: "1px solid #d1d5db",
                  background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}
              >
                No, volver
              </button>
              <button
                onClick={handleCancelarFactura}
                disabled={cancelando}
                style={{
                  padding: "8px 20px", borderRadius: 4, border: "none",
                  background: cancelando ? "#e5e7eb" : "#dc2626",
                  color: cancelando ? "#6b7280" : "#fff",
                  cursor: cancelando ? "wait" : "pointer", fontSize: 13, fontWeight: 700,
                }}
              >
                {cancelando ? "Cancelando..." : "Sí, cancelar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
